/**
 * 5-step tour for producers (role: 'owner' or 'member').
 *
 * Producers may land on the dashboard (`/producer`) or directly on a project,
 * so the spotlight selectors must exist in either place. The dashboard
 * `data-tour="new-project"` is reachable from `/producer`; the
 * project-specific selectors live under `/producer/projects/:id/*` and will
 * gracefully degrade to a centered tooltip if not present on the current
 * page.
 */
import type { TourStep } from '../TourStep'

export const PRODUCER_TOUR: readonly TourStep[] = [
  {
    id: 'producer-welcome',
    title: 'Welcome to OHEAR.AI',
    body: "You're in the Producer console. Five quick steps to show you the recurring loop: spin up a project, approve the script, cast actors, and watch progress live.",
  },
  {
    id: 'producer-new-project',
    selector: '[data-tour="new-project"]',
    title: 'New Project',
    body: 'Start here for a new show. The wizard imports the locked picture, AAF, and CSV, then auto-generates cues, characters, and budgeted line counts.',
    placement: 'bottom',
  },
  {
    id: 'producer-cue-list',
    selector: '[data-tour="cue-list"]',
    title: 'ADR Script Book',
    body: 'Every cue, sortable by scene or by character. Edit line text, split / merge cues, and reassign characters inline. Approve when ready to lock the script for batches.',
    placement: 'top',
  },
  {
    id: 'producer-rate-editor',
    selector: '[data-tour="rate-editor"]',
    title: 'Characters & Contracts',
    body: 'Set per-character line rates and pickup terms. Invite an actor — when they accept, a DocuSign envelope goes out and they unlock for recording.',
    placement: 'top',
  },
  {
    id: 'producer-progress-dashboard',
    selector: '[data-tour="progress-dashboard"]',
    title: 'Live progress',
    body: "The character × batch matrix updates in real time as actors record and supervisors approve. Click any cell to jump into that batch's takes.",
    placement: 'top',
  },
] as const
